/**
 * 네트워크 레이어 실패(오프라인·연결 끊김 등) 판별·재시도 유틸
 *
 * fetch 기반 호출(Supabase 등)은 네트워크가 끊기면 브라우저별로 다른 메시지의 TypeError로 실패한다:
 * - Safari/WKWebView: "Load failed"
 * - Chrome: "Failed to fetch"
 * - Firefox: "NetworkError when attempting to fetch resource."
 *
 * supabase-js는 이 실패를 message 필드에 담은 일반 객체로 감싸기도 하므로 message 문자열 기반으로 판별한다.
 */

const NETWORK_ERROR_PATTERNS = [
  "load failed",
  "failed to fetch",
  "networkerror",
  "network request failed",
  "internet connection appears to be offline",
  "request timed out",
];

/** 에러(Error 또는 message 필드를 가진 객체)에서 메시지 문자열을 추출한다 */
function getErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;

    if (typeof message === "string") {
      return message;
    }
  }

  return undefined;
}

/** 일시적 네트워크 실패(오프라인, 연결 끊김, 타임아웃)인지 판별한다 */
export function isNetworkError(error: unknown): boolean {
  const message = getErrorMessage(error)?.toLowerCase();

  if (!message) {
    return false;
  }

  return NETWORK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
}

/** 네트워크 오류일 때만 지정 횟수만큼 재시도한다. 그 외 오류는 즉시 throw */
export async function retryOnNetworkError<T>(
  fn: () => Promise<T>,
  { retries = 1, delayMs = 700 }: { retries?: number; delayMs?: number } = {},
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (!isNetworkError(error) || attempt === retries) {
        throw error;
      }

      lastError = error;
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  throw lastError;
}
